import * as React from 'react';
import { usePortalConnections } from 'vet-bones/utils/portal';

import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

/**
 * Setup default connection (depending on portal url index) on state,
 * and optionally try connecting.
 */
export const useSetupDefaultPortalConnection = ({
  connect = true,
  connectWithoutToken,
  shouldNotNotifySuccess,
}: {
  /**
   * Whether to try connecting immediately.
   *
   * This will only connect fully if a token was provided, otherwise
   * it will just setup the connection state and not show a SignInDialog.
   *
   * This is because showing the SignInDialog popup would be abrupt.
   */
  connect?: boolean;
  connectWithoutToken?: boolean;
  shouldNotNotifySuccess?: boolean;
} = {}) => {
  const dispatch = useReduxDispatch();
  const portalConnections = usePortalConnections();
  const defaultPortalConnection = useDefaultPortalConnection();

  // only connect once
  const didDefaultConnection = React.useRef(false);

  // automatically connect to the "current portal connection" via url index
  const { isFetched } = portalConnections;
  React.useEffect(() => {
    if (
      didDefaultConnection.current ||
      !isFetched ||
      !defaultPortalConnection
    ) {
      return;
    }
    didDefaultConnection.current = true;

    // Don't try connecting if we don't have a token,
    // as this would show a SignInDialog abruptly (asking for a password).
    //
    // Don't try connecting if we couldn't gather url info
    // since we have no way of knowing which was the correct connection.
    const shouldConnect = Boolean(
      connect && (connectWithoutToken || defaultPortalConnection.token)
    );
    dispatch(
      setupPortalConnection(defaultPortalConnection, {
        connect: shouldConnect,
        shouldNotNotifySuccess,
      })
    );
  }, [
    connect,
    defaultPortalConnection,
    dispatch,
    isFetched,
    shouldNotNotifySuccess,
    connectWithoutToken,
  ]);
};
